import { getAccessToken, refreshAccessToken } from '../utils/authToken';
import { invalidateSession } from '../utils/authSession';
import { pdfBlobFromResponseBody, reportBlobFromResponseBody } from '../utils/pdfResponse';
import { toUserFacingErrorMessage } from '../utils/userFacingError';

async function getAuthHeader(): Promise<Record<string, string> | null> {
  const token = await getAccessToken();
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

type AuthRetryState = { _authRetry?: boolean };

async function handleUnauthorizedResponse(status: number): Promise<void> {
  if (status === 401) {
    invalidateSession();
  }
  // 403 = permission denied, session is valid — do not invalidate
}

export const commonHeaders = {
  'application-code': 'INTRANET',
  // Some backends/specs use camelCase header; keep both for compatibility.
  applicationCode: 'INTRANET',
} as const;

type ApiOk<T> = { ok: true; data: T; status: number };
type ApiErr = { ok: false; status: number; message: string; raw?: unknown; aborted?: boolean };

export type ApiResponse<T> = ApiOk<T> | ApiErr;

function normalizeApplicationCodeHeaderPair(headers: Record<string, string>): Record<string, string> {
  const ac = headers.applicationCode?.trim();
  const ak = headers['application-code']?.trim();
  const code = ac || ak;
  if (!code) return headers;

  // Always force both spellings to the same value for backend compatibility.
  headers.applicationCode = code;
  headers['application-code'] = code;
  return headers;
}

function dropBlankHeaders(headers: Record<string, string> | undefined): Record<string, string> | undefined {
  if (!headers) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (typeof v !== 'string') continue;
    const trimmed = v.trim();
    if (!trimmed) continue;
    out[k] = trimmed;
  }
  return normalizeApplicationCodeHeaderPair(out);
}

function getBaseUrl(): string {
  const base = (import.meta.env.VITE_API_BASE_URL ?? '').trim();
  if (base) return base.replace(/\/+$/, '');
  // Default to Vite proxy path when not configured.
  return '/api';
}

/**
 * Case management service (CMS-CRC-* case-request endpoints).
 *
 * In local dev, prefer the Vite proxy at `/case-management` so the browser stays same-origin
 * (see `vite.config.ts`). Set `VITE_CASE_API_DIRECT=true` only when you intentionally want the
 * browser to call `VITE_CASE_API_BASE_URL` directly (e.g. CORS already allows your dev origin).
 */
export function getCaseManagementBaseUrl(): string {
  const raw = (import.meta.env.VITE_CASE_API_BASE_URL ?? '').trim();
  const directInDev = import.meta.env.VITE_CASE_API_DIRECT === 'true';

  if (import.meta.env.DEV && !directInDev) {
    return '/case-management';
  }

  if (raw) return raw.replace(/\/+$/, '');
  return 'https://qa-case-mgt-service-agd.inova.lk/case-management';
}

function getTimeoutMs(): number {
  const raw = (import.meta.env.VITE_REQUEST_TIMEOUT_MS ?? '').trim();
  const n = raw ? Number(raw) : 30000;
  return Number.isFinite(n) && n > 0 ? n : 30000;
}

function isJsonResponse(res: Response): boolean {
  const ct = res.headers.get('content-type') ?? '';
  return ct.includes('application/json') || ct.includes('+json');
}

async function safeReadBody(res: Response): Promise<unknown> {
  try {
    if (isJsonResponse(res)) return await res.json();
    const t = await res.text();
    return t ? { text: t } : null;
  } catch {
    return null;
  }
}

function statusFallbackMessage(status: number): string {
  if (status === 0) return 'Network error.';
  if (status === 413) return 'The file is too large. Please choose a smaller file.';
  if (status >= 500) return 'Server error.';
  return 'Request failed.';
}

function normalizeErrorMessage(status: number, body: unknown): string {
  const fallback = statusFallbackMessage(status);

  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const meta = b.meta && typeof b.meta === 'object' ? (b.meta as Record<string, unknown>) : null;
    const apiStatus = b.api_status && typeof b.api_status === 'object' ? (b.api_status as Record<string, unknown>) : null;

    const errs = apiStatus?.error;
    if (Array.isArray(errs) && errs.length) {
      const first = errs[0] as Record<string, unknown>;
      const msg = first?.errorMessage;
      if (typeof msg === 'string' && msg.trim()) {
        return toUserFacingErrorMessage(msg, fallback);
      }
    }

    // Fallback to meta.message only if api_status didn't provide a specific error.
    const metaMsg = meta?.message;
    if (typeof metaMsg === 'string' && metaMsg.trim()) {
      return toUserFacingErrorMessage(metaMsg, fallback);
    }
  }

  return fallback;
}

/**
 * Some services return HTTP 200 with `api_status` describing real success/failure.
 * When `api_status` is present, non-SUCCESS status or a non-empty `error` array means failure.
 */
/** After HTTP + api_status pass, some endpoints still return `data.status` for operation outcome. */
export function isDataPayloadSuccess(data: unknown): boolean {
  if (!data || typeof data !== 'object') return true;
  const st = (data as Record<string, unknown>).status;
  if (typeof st !== 'string' || !st.trim()) return true;
  const s = st.trim().toLowerCase();
  return s === 'success' || s === 'ok' || s === 'true';
}

export function dataPayloadErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return 'Request failed.';
  const msg = (data as Record<string, unknown>).message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return 'Request failed.';
}

function isApiEnvelopeLogicalError(body: unknown): boolean {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
  const b = body as Record<string, unknown>;
  const apiStatus = b.api_status;
  if (!apiStatus || typeof apiStatus !== 'object') return false;
  const as = apiStatus as Record<string, unknown>;
  const errs = as.error;
  if (Array.isArray(errs) && errs.length > 0) return true;
  const st = as.status;
  if (typeof st !== 'string' || !st.trim()) return false;
  return st.trim().toUpperCase() !== 'SUCCESS';
}

export type ApiRequestBody = unknown | FormData;

export async function apiRequest<T>(
  path: string,
  opts?: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: ApiRequestBody;
    signal?: AbortSignal;
    headers?: Record<string, string>;
    baseUrl?: string;
  } & AuthRetryState,
): Promise<ApiResponse<T>> {
  const base = (opts?.baseUrl ?? getBaseUrl()).replace(/\/+$/, '');
  const url = `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), getTimeoutMs());

  try {
    // Prevent accidental overrides like { applicationCode: '' } from wiping common headers.
    const cleanedExtraHeaders = dropBlankHeaders(opts?.headers);
    const isFormData = opts?.body instanceof FormData;
    const authHeader = await getAuthHeader();
    // Merge headers with safe defaults, but allow endpoint-specific `applicationCode`.
    const mergedHeaders = normalizeApplicationCodeHeaderPair({
      ...commonHeaders,
      Accept: 'application/json',
      ...(authHeader ?? null),
      ...(opts?.body != null && !isFormData ? { 'Content-Type': 'application/json' } : null),
      ...(cleanedExtraHeaders ?? null),
    });

    let requestBody: BodyInit | undefined;
    if (opts?.body instanceof FormData) {
      requestBody = opts.body;
    } else if (opts?.body != null) {
      requestBody = JSON.stringify(opts.body);
    }

    const res = await fetch(url, {
      method: opts?.method ?? 'GET',
      headers: mergedHeaders,
      body: requestBody,
      signal: opts?.signal ? AbortSignal.any([opts.signal, controller.signal]) : controller.signal,
    });

    const body = await safeReadBody(res);

    if (!res.ok) {
      if (res.status === 401 && !opts?._authRetry) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          return apiRequest<T>(path, { ...opts, _authRetry: true });
        }
        await handleUnauthorizedResponse(res.status);
      } else if (res.status === 401) {
        await handleUnauthorizedResponse(res.status);
      }
      // 403 = permission denied — return error, no retry, no session invalidation
      return { ok: false, status: res.status, message: normalizeErrorMessage(res.status, body), raw: body };
    }

    // OpenAPI list endpoints often return a raw JSON array (e.g. case-request-sub-categories).
    if (Array.isArray(body)) {
      return { ok: true, status: res.status, data: body as T };
    }

    if (isApiEnvelopeLogicalError(body)) {
      return { ok: false, status: res.status, message: normalizeErrorMessage(res.status, body), raw: body };
    }

    // Many endpoints return the resource directly; some wrap in {data: ...}
    if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
      return { ok: true, status: res.status, data: (body as { data: T }).data };
    }

    return { ok: true, status: res.status, data: body as T };
  } catch (e: unknown) {
    // Caller abort (tab change / React remount) — not a real failure or timeout.
    if (opts?.signal?.aborted) {
      return { ok: false, status: 0, message: 'Aborted.', aborted: true };
    }
    const msg = e instanceof Error && e.name === 'AbortError' ? 'Request timed out.' : 'Network error.';
    return { ok: false, status: 0, message: msg };
  } finally {
    clearTimeout(timeout);
  }
}

/** Case management JSON API — paths are normalized under `/v1/case-requests`. */
export function caseApiRequest<T>(
  path: string,
  opts?: Omit<NonNullable<Parameters<typeof apiRequest<T>>[1]>, 'baseUrl'>,
): Promise<ApiResponse<T>> {
  const normalized =
    path.startsWith('/v1/') || path.startsWith('/v2/')
      ? path
      : `/v1/case-requests${path.startsWith('/') ? path : `/${path}`}`;
  return apiRequest<T>(normalized, { ...opts, baseUrl: getCaseManagementBaseUrl() });
}

/** Workflow template API — paths are normalized under `/v1/workflow-templates`. */
export function workflowApiRequest<T>(
  path: string,
  opts?: Omit<NonNullable<Parameters<typeof apiRequest<T>>[1]>, 'baseUrl'>,
): Promise<ApiResponse<T>> {
  const normalized = path.startsWith('/v1/workflow-templates')
    ? path
    : `/v1/workflow-templates${path.startsWith('/') ? path : path ? `/${path}` : ''}`;
  return apiRequest<T>(normalized, { ...opts, baseUrl: getCaseManagementBaseUrl() });
}

/** Case scenario API — paths are normalized under `/v1/case-scenarios`. */
export function caseScenarioApiRequest<T>(
  path: string,
  opts?: Omit<NonNullable<Parameters<typeof apiRequest<T>>[1]>, 'baseUrl'>,
): Promise<ApiResponse<T>> {
  const normalized = path.startsWith('/v1/case-scenarios')
    ? path
    : `/v1/case-scenarios${path.startsWith('/') ? path : `/${path}`}`;
  return apiRequest<T>(normalized, { ...opts, baseUrl: getCaseManagementBaseUrl() });
}

/** Reports API — paths are normalized under `/v1/reports`. */
export function reportsApiRequest<T>(
  path: string,
  opts?: Omit<NonNullable<Parameters<typeof apiRequest<T>>[1]>, 'baseUrl'>,
): Promise<ApiResponse<T>> {
  const normalized = path.startsWith('/v1/reports')
    ? path
    : `/v1/reports${path.startsWith('/') ? path : `/${path}`}`;
  return apiRequest<T>(normalized, { ...opts, baseUrl: getCaseManagementBaseUrl() });
}

/** SG / operational dashboard API — CMS-SGD-* under `/v1/dashboard`. */
export function dashboardApiRequest<T>(
  path: string,
  opts?: Omit<NonNullable<Parameters<typeof apiRequest<T>>[1]>, 'baseUrl'>,
): Promise<ApiResponse<T>> {
  const normalized = path.startsWith('/v1/dashboard')
    ? path
    : `/v1/dashboard${path.startsWith('/') ? path : `/${path}`}`;
  return apiRequest<T>(normalized, { ...opts, baseUrl: getCaseManagementBaseUrl() });
}

export type ReportExportFile = {
  blob: Blob;
  fileName?: string;
};

/** Reports export — POST JSON filters, receive PDF/Excel binary (or JSON base64). */
export async function reportsApiExportRequest(
  path: string,
  body: ApiRequestBody,
  opts?: { signal?: AbortSignal } & AuthRetryState,
): Promise<ApiResponse<ReportExportFile>> {
  const normalized = path.startsWith('/v1/reports')
    ? path
    : `/v1/reports${path.startsWith('/') ? path : `/${path}`}`;
  const base = getCaseManagementBaseUrl();
  const url = `${base}${normalized}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), getTimeoutMs());

  try {
    const authHeader = await getAuthHeader();
    const mergedHeaders = normalizeApplicationCodeHeaderPair({
      ...commonHeaders,
      Accept: 'application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json, */*',
      'Content-Type': 'application/json',
      ...(authHeader ?? {}),
    });

    const res = await fetch(url, {
      method: 'POST',
      headers: mergedHeaders,
      body: JSON.stringify(body),
      signal: opts?.signal ? AbortSignal.any([opts.signal, controller.signal]) : controller.signal,
    });

    if (!res.ok) {
      const responseBody = await safeReadBody(res);
      if (res.status === 401 && !opts?._authRetry) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          return reportsApiExportRequest(path, body, { ...opts, _authRetry: true });
        }
        await handleUnauthorizedResponse(res.status);
      } else if (res.status === 401) {
        await handleUnauthorizedResponse(res.status);
      }
      // 403 = permission denied — return error, no retry, no session invalidation
      return { ok: false, status: res.status, message: normalizeErrorMessage(res.status, responseBody), raw: responseBody };
    }

    const contentType = res.headers.get('content-type') ?? '';
    const buffer = await res.arrayBuffer();
    if (!buffer.byteLength) {
      return { ok: false, status: res.status, message: 'File is empty or unavailable.', raw: null };
    }

    try {
      const lower = contentType.toLowerCase();
      if (
        lower.includes('pdf') ||
        lower.includes('json') ||
        lower.includes('text') ||
        lower.includes('spreadsheetml') ||
        lower.includes('excel') ||
        lower.includes('sheet')
      ) {
        const { blob, fileName } = reportBlobFromResponseBody(buffer, contentType);
        if (!blob.size) {
          return { ok: false, status: res.status, message: 'File is empty or unavailable.', raw: null };
        }
        return { ok: true, status: res.status, data: { blob, fileName } };
      }

      const blob = new Blob([buffer], {
        type:
          contentType ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      if (!blob.size) {
        return { ok: false, status: res.status, message: 'File is empty or unavailable.', raw: null };
      }
      return { ok: true, status: res.status, data: { blob } };
    } catch (e: unknown) {
      const msg = e instanceof Error && e.message ? e.message : 'Could not download report.';
      return { ok: false, status: res.status, message: msg, raw: null };
    }
  } catch (e: unknown) {
    const msg = e instanceof Error && e.name === 'AbortError' ? 'Request timed out.' : 'Network error.';
    return { ok: false, status: 0, message: msg };
  } finally {
    clearTimeout(timeout);
  }
}

/** CMS-CRC-5 — cover note PDF (binary or JSON/base64). */
export async function caseApiPdfRequest(
  path: string,
  opts?: { signal?: AbortSignal } & AuthRetryState,
): Promise<ApiResponse<Blob>> {
  const normalized = path.startsWith('/v1/') ? path : `/v1/case-requests${path.startsWith('/') ? path : `/${path}`}`;
  const base = getCaseManagementBaseUrl();
  const url = `${base}${normalized}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), getTimeoutMs());

  try {
    const authHeader = await getAuthHeader();
    const mergedHeaders = normalizeApplicationCodeHeaderPair({
      ...commonHeaders,
      Accept: 'application/pdf, application/json, text/plain, */*',
      ...(authHeader ?? {}),
    });

    const res = await fetch(url, {
      method: 'GET',
      headers: mergedHeaders,
      signal: opts?.signal ? AbortSignal.any([opts.signal, controller.signal]) : controller.signal,
    });

    if (!res.ok) {
      const body = await safeReadBody(res);
      if (res.status === 401 && !opts?._authRetry) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          return caseApiPdfRequest(path, { ...opts, _authRetry: true });
        }
        await handleUnauthorizedResponse(res.status);
      } else if (res.status === 401) {
        await handleUnauthorizedResponse(res.status);
      }
      // 403 = permission denied — return error, no retry, no session invalidation
      return { ok: false, status: res.status, message: normalizeErrorMessage(res.status, body), raw: body };
    }

    const contentType = res.headers.get('content-type') ?? '';
    const buffer = await res.arrayBuffer();
    if (!buffer.byteLength) {
      return { ok: false, status: res.status, message: 'File is empty or unavailable.', raw: null };
    }

    try {
      const blob = pdfBlobFromResponseBody(buffer, contentType);
      if (!blob.size) {
        return { ok: false, status: res.status, message: 'File is empty or unavailable.', raw: null };
      }
      return { ok: true, status: res.status, data: blob };
    } catch (e: unknown) {
      const msg = e instanceof Error && e.message ? e.message : 'Could not load PDF.';
      return { ok: false, status: res.status, message: msg, raw: null };
    }
  } catch (e: unknown) {
    const msg = e instanceof Error && e.name === 'AbortError' ? 'Request timed out.' : 'Network error.';
    return { ok: false, status: 0, message: msg };
  } finally {
    clearTimeout(timeout);
  }
}

